/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

'use client';

import { BackArrow } from '@ahamove/icons';
import { ErrorCode } from '@ahm/api-wrappers-core';
import { local } from '@ahm/common-helpers';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Anchor,
  Box,
  Center,
  Group,
  PinInput,
  Space,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '../auth-provider';
import {
  AuthStep,
  LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD,
} from '../lib/constants';
import { getRemainCountDownTime, hidePhoneNumber } from '../lib/utils';
import { authLocales, createTranslations, type Locales } from '../locales';
import { useAuthStore } from '../store/auth-store-provider';
import { SmsIcon, ZaloIcon } from './icons';

const otpSchema = z.object({
  otp: z.string().length(4, 'OTP must be 4 digits'),
});

type OtpFormValues = z.infer<typeof otpSchema>;

type OtpFormProps = {
  lang?: keyof Locales;
};
export default function OtpForm({ lang = 'vi' }: OtpFormProps) {
  const tAuth = createTranslations({ locales: authLocales, lang });

  const { apiError, activeAuthStep, setActiveAuthStep, setApiError } =
    useAuthStore((state) => state);

  const {
    loading,
    handleActivateUser,
    loginData,
    onReSendOTP,
    isForceLoginOTP,
  } = useAuth();

  const [isShowResendBtn, setIsShowResendBtn] = useState(false);
  const [timeResendOTP, setTimeResendOTP] = useState(getRemainCountDownTime());
  const {
    control,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
  } = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: '',
    },
  });

  useEffect(() => {
    if (activeAuthStep === AuthStep.OTP || apiError || !loading) {
      setFocus('otp');
      if (apiError) {
        reset();
      }
    }
  }, [activeAuthStep, apiError, reset, loading, setFocus]);

  const descriptionViaOtpMethod = useMemo(() => {
    if (loginData.otp_method === 'zalo') return tAuth('zaloOtpNote');
    if (loginData.otp_method === 'sms') return tAuth('smsOtpNote');
    return '';
  }, [loginData]);

  const iconViaOtpMethod = useMemo(() => {
    if (loginData.otp_method === 'zalo') {
      return <ZaloIcon width={32} height={32} />;
    }
    if (loginData.otp_method === 'sms') {
      return <SmsIcon width={32} height={32} />;
    }
    return null;
  }, [loginData]);

  const handleReSendOTP = () => {
    reset();
    const prevSentCodePayload = local.getItem(
      LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD
    );
    if (prevSentCodePayload) {
      const timeLastSend = JSON.parse(prevSentCodePayload);
      if (!timeLastSend) return;

      onReSendOTP(timeLastSend._id);
      setIsShowResendBtn(false);
    }
  };

  const handleBack = () => {
    setApiError(null);
    reset();
    setActiveAuthStep(AuthStep.PhoneNumber);
  };

  const onSubmit = (data: OtpFormValues) => {
    handleActivateUser(data.otp);
  };

  const handleOtpComplete = (value: string) => {
    if (value.length === 4) {
      handleActivateUser(value);
    }
  };

  useEffect(() => {
    if (activeAuthStep !== AuthStep.OTP) return;
    if (timeResendOTP <= 0) return;
    const intervalId = setInterval(() => {
      setTimeResendOTP(getRemainCountDownTime());
      if (timeResendOTP - 1 <= 0) {
        if (intervalId) {
          clearInterval(intervalId);
        }
        setIsShowResendBtn(true);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [activeAuthStep, timeResendOTP]);

  return (
    <Box
      display="flex"
      w="100%"
      flex={1}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      onReset={() => reset()}
      pb="xs"
      style={{ flexDirection: 'column' }}
    >
      <Stack align="stretch" flex={1} gap="sm">
        <Group wrap="nowrap" hiddenFrom="sm">
          <ActionIcon
            size={rem(44)}
            radius="xl"
            variant="subtle"
            color="neutral"
            aria-label="Back"
            my={rem(-10)}
            ml={rem(-10)}
            onClick={handleBack}
          >
            <BackArrow width={24} height={24} />
          </ActionIcon>
        </Group>

        <Group gap={8}>
          {iconViaOtpMethod}
          <Title order={2}>
            {isForceLoginOTP ? tAuth('loginWithOTP') : tAuth('enterOTP')}
          </Title>
        </Group>
        <Text size="md" c="neutral.6" fw={500}>
          {descriptionViaOtpMethod}
          <Text component="span" size="md" fw={700} c="neutral.7">
            {` +${hidePhoneNumber(loginData.id)}`}
          </Text>
        </Text>
        <Stack gap={0} w="100%" mt="sm">
          <Controller
            name="otp"
            control={control}
            render={({ field: { onChange, value, ...props } }) => (
              <PinInput
                {...props}
                // disabled={loading}
                length={4}
                gap={rem(8)}
                inputMode="numeric"
                size="lg"
                placeholder=""
                value={value}
                onChange={onChange}
                onComplete={handleOtpComplete}
                error={!!errors.otp}
                oneTimeCode
                aria-label="One time code"
                styles={{
                  root: {
                    '--mantine-color-gray-4': 'var(--mantine-color-neutral-0)',
                  },
                  input: {
                    backgroundColor: 'var(--mantine-color-neutral-0)',
                  },
                }}
              />
            )}
          />

          <Text fw={500} size="sm" c="red.5" my={rem(8)} mih={rem(20)}>
            {errors.otp?.message ?? apiError?.description ?? apiError?.title}
          </Text>
          <Space h={rem(44)} w="100%" />
          {apiError?.code !== ErrorCode.BannedUser ? (
            <>
              <Title order={5}>{tAuth('didNotReceiveOtp')}</Title>
              {isShowResendBtn ? (
                <Anchor
                  size="md"
                  fw={700}
                  onClick={handleReSendOTP}
                  mt={rem(4)}
                >
                  {tAuth('resendOtp')}
                </Anchor>
              ) : (
                <Text size="md" c="neutral.6" mih={rem(32)}>
                  {tAuth('resendOtpIn')}
                  <Title order={5} component="time" c="primary.6">
                    {timeResendOTP}s
                  </Title>
                </Text>
              )}
            </>
          ) : null}
        </Stack>

        <Space h={rem(32)} w="100%" />

        <Center mt="auto">
          <Anchor size="sm" fw={600} onClick={handleBack} c="red.6">
            {tAuth('changePhoneNumber')}
          </Anchor>
        </Center>
      </Stack>
    </Box>
  );
}
